/**
 * SegremaWebAlunniGenitori — Archivio dei genitori per CF Alunno.
 *
 * - Tabella ricercabile dei genitori importati dal file XLSX di Segremat (modello AG1E2)
 * - Upload XLSX → sostituzione totale dell'archivio (drop + insert)
 * - Mostra metadati ultimo import (data, utente, file, totali)
 * - Search testuale su CF alunno, CF genitori, cognomi, nomi, email, telefoni
 * - Lookup veloce per CF alunno con bottone "🔍 Trova per CF"
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAnnoScolastico } from "@/lib/annoScolastico";
import AnnoAttivoSelector from "@/components/AnnoAttivoSelector";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

export default function SegremaWebAlunniGenitori() {
  const { anno: annoGlobal, setAnno: setAnnoGlobal } = useAnnoScolastico();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [lastImport, setLastImport] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [filterHasEmail, setFilterHasEmail] = useState(false);
  const [filterHasPhone, setFilterHasPhone] = useState(false);
  // 🆕 Filtro anno: si inizializza dal contesto globale A.S.
  const [filterAnno, setFilterAnno] = useState(annoGlobal ?? null);
  // Sync anno con global A.S.
  useEffect(() => {
    if (annoGlobal != null && annoGlobal !== filterAnno) setFilterAnno(annoGlobal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annoGlobal]);
  useEffect(() => {
    if (filterAnno != null && filterAnno !== annoGlobal) setAnnoGlobal(filterAnno);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAnno]);

  // 🆕 Rilevamento FRATELLI: raggruppa gli alunni per CF genitore condiviso.
  // Due alunni sono considerati fratelli se hanno almeno un CF genitore in comune
  // (g1 o g2). Il gruppo viene identificato dal minimo CF alunno tra i fratelli.
  const siblingsInfo = useMemo(() => {
    // Costruisci mappa: cf_genitore → set{cf_alunno}
    const parentToStudents = new Map();
    for (const it of items) {
      const cfa = String(it.cf_alunno || "").toUpperCase().trim();
      if (!cfa) continue;
      for (const key of ["g1_cf", "g2_cf"]) {
        const gcf = String(it[key] || "").toUpperCase().trim();
        if (!gcf || gcf.length < 8) continue; // ignora CF vuoti/troppo corti
        if (!parentToStudents.has(gcf)) parentToStudents.set(gcf, new Set());
        parentToStudents.get(gcf).add(cfa);
      }
    }
    // Union-Find semplice sugli alunni
    const parent = new Map();
    const find = (x) => {
      while (parent.get(x) !== x) {
        parent.set(x, parent.get(parent.get(x)));
        x = parent.get(x);
      }
      return x;
    };
    const union = (a, b) => {
      const ra = find(a), rb = find(b);
      if (ra !== rb) parent.set(ra, rb);
    };
    for (const it of items) {
      const cfa = String(it.cf_alunno || "").toUpperCase().trim();
      if (!cfa) continue;
      if (!parent.has(cfa)) parent.set(cfa, cfa);
    }
    for (const [, students] of parentToStudents) {
      if (students.size < 2) continue;
      const arr = Array.from(students);
      for (let k = 1; k < arr.length; k++) union(arr[0], arr[k]);
    }
    // Conta membri per gruppo + assegna colore
    const groupMembers = new Map();
    for (const cfa of parent.keys()) {
      const r = find(cfa);
      if (!groupMembers.has(r)) groupMembers.set(r, []);
      groupMembers.get(r).push(cfa);
    }
    // Solo gruppi con >=2 membri sono "famiglie"
    const family = new Map(); // cf_alunno → { count, groupId, color }
    const palette = ["#fff3e0", "#e8f5e9", "#e3f2fd", "#fce4ec", "#f3e5f5", "#fff9c4", "#e0f7fa"];
    const paletteBorder = ["#ef6c00", "#2e7d32", "#1565c0", "#c2185b", "#6a1b9a", "#f57f17", "#00838f"];
    let groupIdx = 0;
    for (const [root, members] of groupMembers) {
      if (members.length < 2) continue;
      const colorBg = palette[groupIdx % palette.length];
      const colorBorder = paletteBorder[groupIdx % paletteBorder.length];
      for (const cfa of members) family.set(cfa, { count: members.length, groupId: groupIdx + 1, color: colorBg, border: colorBorder, siblings: members.filter(m => m !== cfa) });
      groupIdx++;
    }
    return family;
  }, [items]);

  const carica = async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (filterHasEmail) params.set("has_email", "true");
      if (filterHasPhone) params.set("has_phone", "true");
      if (filterAnno != null) params.set("anno", String(filterAnno));
      const r = await api.get(`/alunni-genitori?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total_archivio || 0);
      setLastImport(r.data?.last_import || null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { carica(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterAnno, filterHasEmail, filterHasPhone]);

  const onSearchSubmit = (e) => { e.preventDefault(); carica(); };

  const handleUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    input.style.display = "none";
    document.body.appendChild(input);
    const file = await new Promise((resolve) => {
      input.onchange = () => resolve(input.files?.[0] || null);
      input.click();
      setTimeout(() => resolve(null), 90000);
    });
    document.body.removeChild(input);
    if (!file) return;

    if (!window.confirm(
      `📥 Importo "${file.name}" (${(file.size/1024).toFixed(0)} KB)?\n\n` +
      `⚠ ATTENZIONE: l'archivio attuale (${total} record) verrà SOSTITUITO completamente.\n\n` +
      `Continuare?`
    )) return;

    setUploading(true); setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/alunni-genitori/import-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(r.data || null);
      await carica();
    } catch (e) {
      alert(`Errore upload: ${e.response?.data?.detail || e.message}`);
    } finally { setUploading(false); }
  };

  const onTruncate = async () => {
    if (!window.confirm("⚠ Eliminare TUTTI i record dell'archivio? Operazione irreversibile.")) return;
    try {
      await api.delete("/alunni-genitori/_admin/truncate");
      setImportReport(null);
      await carica();
    } catch (e) {
      alert(`Errore: ${e.response?.data?.detail || e.message}`);
    }
  };

  const onDeleteRow = async (id, cf) => {
    if (!window.confirm(`Eliminare il record per CF ${cf}?`)) return;
    try {
      await api.delete(`/alunni-genitori/${id}`);
      await carica();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const counters = useMemo(() => {
    const conMail = items.filter(it => it.g1_email || it.g2_email).length;
    const conTel = items.filter(it => it.g1_telefono || it.g2_telefono).length;
    return { conMail, conTel };
  }, [items]);

  return (
    <div style={{ padding: 16 }} data-testid="segremaweb-alunni-genitori">
      <div style={{ background: "linear-gradient(90deg,#1a237e,#283593)", color: "white", padding: "12px 18px", marginBottom: 12, borderRadius: 4, fontSize: 14, fontWeight: 800, letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span>👨‍👩‍👧 ALUNNI ›› GENITORI · Archivio dati di contatto da Segremat (XLSX)</span>
        {lastImport && (
          <span style={{ marginLeft: "auto", fontWeight: 400, fontSize: 11, opacity: 0.9 }}>
            Ultimo import: {fmtDateTime(lastImport.timestamp)} · {lastImport.totale_caricati} record · {lastImport.imported_by || "—"} · <i>{lastImport.source_file || "—"}</i>
          </span>
        )}
      </div>

      {/* Toolbar */}
      <div style={{ background: "#f5f5f5", padding: 10, marginBottom: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderRadius: 4 }}>
        <form onSubmit={onSearchSubmit} style={{ display: "flex", gap: 6, flex: "1 1 320px" }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Cerca cognome/nome alunno, CF, email, telefono, CF/cognome/nome genitore..."
            data-testid="ag-search"
            style={{ flex: 1, padding: "6px 10px", border: "1px solid #bbb", fontSize: 12, borderRadius: 3 }}
          />
          <button type="submit"
            style={{ padding: "6px 14px", background: "#1a237e", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            Cerca
          </button>
          {search && (
            <button type="button" onClick={() => { setSearch(""); setTimeout(carica, 50); }}
              style={{ padding: "6px 10px", background: "#9e9e9e", color: "white", border: "none", fontSize: 11, cursor: "pointer", borderRadius: 3 }}>
              ✕
            </button>
          )}
        </form>
        {/* 🆕 Selettore AS unificato (feb 2026) */}
        <AnnoAttivoSelector />
        <label style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
          <input type="checkbox" data-testid="ag-filter-email" checked={filterHasEmail}
            onChange={(e) => { setFilterHasEmail(e.target.checked); setTimeout(carica, 50); }} />
          ✉ con email
        </label>
        <label style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
          <input type="checkbox" data-testid="ag-filter-phone" checked={filterHasPhone}
            onChange={(e) => { setFilterHasPhone(e.target.checked); setTimeout(carica, 50); }} />
          📞 con telefono
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button onClick={handleUpload} disabled={uploading}
            data-testid="ag-upload"
            style={{ padding: "6px 14px", background: uploading ? "#bdbdbd" : "#2e7d32", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: uploading ? "wait" : "pointer", borderRadius: 3 }}>
            {uploading ? "⏳ caricamento…" : "📥 Importa XLSX (sostituisce tutto)"}
          </button>
          <button onClick={onTruncate}
            data-testid="ag-truncate"
            title="Svuota completamente l'archivio"
            style={{ padding: "6px 12px", background: "#c62828", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            🗑 Svuota
          </button>
        </div>
      </div>

      {/* Counters */}
      <div style={{ background: "white", border: "1px solid #ddd", borderRadius: 4, padding: "8px 12px", marginBottom: 8, display: "flex", gap: 18, fontSize: 12, flexWrap: "wrap" }}>
        <span><strong style={{ color: "#1a237e", fontSize: 16 }}>{total}</strong> record in archivio</span>
        <span><strong style={{ color: "#0d47a1", fontSize: 16 }}>{items.length}</strong> mostrati</span>
        <span style={{ color: "#1b5e20" }}>✉ {counters.conMail} con email</span>
        <span style={{ color: "#0277bd" }}>📞 {counters.conTel} con telefono</span>
      </div>

      {importReport && (
        <div style={{ background: "#e8f5e9", border: "1px solid #2e7d32", padding: 10, marginBottom: 8, borderRadius: 4, fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: "#1b5e20", marginBottom: 4 }}>✓ Import completato</div>
          <div>• Record caricati: <b>{importReport.totale_caricati}</b></div>
          <div>• Genitori validi (almeno uno): <b>{importReport.totale_genitori_validi}</b></div>
          {importReport.righe_scartate_count > 0 && (
            <div style={{ color: "#c62828" }}>⚠ Righe scartate: {importReport.righe_scartate_count} (esempi: {importReport.righe_scartate?.slice(0,3).map(r => `riga ${r.riga} (${r.motivo})`).join("; ")})</div>
          )}
          {importReport.header_mancanti?.length > 0 && (
            <div style={{ color: "#ef6c00" }}>⚠ Header mancanti: {importReport.header_mancanti.join(", ")}</div>
          )}
        </div>
      )}

      {err && <div style={{ background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, marginBottom: 8, fontSize: 12, borderRadius: 3 }}>{err}</div>}

      {/* Tabella */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", background: "white" }}>
          <thead>
            <tr style={{ background: "#1a237e", color: "white" }}>
              <th style={{ ...th, textAlign: "left" }}>ALUNNO</th>
              <th style={{ ...th, textAlign: "left" }}>CF ALUNNO</th>
              <th style={{ ...th, textAlign: "left", background: "#283593" }} colSpan={4}>👤 GENITORE 1</th>
              <th style={{ ...th, textAlign: "left", background: "#3949ab" }} colSpan={4}>👤 GENITORE 2</th>
              <th style={{ ...th, textAlign: "center" }}>AZIONI</th>
            </tr>
            <tr style={{ background: "#3949ab", color: "white", fontSize: 9 }}>
              <th style={th2}>Cognome Nome · Classe</th>
              <th style={th2}></th>
              <th style={th2}>Cognome Nome</th>
              <th style={th2}>CF</th>
              <th style={th2}>✉ Email</th>
              <th style={th2}>📞 Tel</th>
              <th style={th2}>Cognome Nome</th>
              <th style={th2}>CF</th>
              <th style={th2}>✉ Email</th>
              <th style={th2}>📞 Tel</th>
              <th style={th2}></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} style={{ textAlign: "center", padding: 20, color: "#888" }}>Caricamento…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={11} style={{ textAlign: "center", padding: 30, color: "#888", fontStyle: "italic" }}>
                Nessun record. {total === 0 ? "L'archivio è vuoto: usa \"📥 Importa XLSX\" per caricare il file da Segremat." : "Nessun match con i filtri impostati."}
              </td></tr>
            )}
            {!loading && items.map((it, i) => {
              const cfa = String(it.cf_alunno || "").toUpperCase().trim();
              const fam = siblingsInfo.get(cfa);
              const isSibling = !!fam;
              return (
              <tr key={it._id} data-testid={`ag-row-${i}`}
                style={{ borderTop: "1px solid #eee",
                         background: isSibling ? fam.color : (i % 2 ? "#fafafa" : "white"),
                         borderLeft: isSibling ? `4px solid ${fam.border}` : "4px solid transparent" }}>
                <td style={{ padding: "6px 8px", maxWidth: 180 }}>
                  {it.alunno_cognome || it.alunno_nome ? (
                    <>
                      <div style={{ fontWeight: 800, color: "#1a237e", fontSize: 12 }}>
                        {it.alunno_cognome || ""} {it.alunno_nome || ""}
                        {isSibling && (
                          <span data-testid={`ag-sibling-badge-${i}`}
                            title={`Fratelli/sorelle nell'archivio: ${fam.count} (gruppo #${fam.groupId})\nCF fratelli: ${fam.siblings.join(", ")}`}
                            style={{ marginLeft: 6, background: fam.border, color: "white", padding: "1px 6px", fontSize: 9, fontWeight: 800, borderRadius: 2, letterSpacing: 0.3 }}>
                            👪 {fam.count} FRATELLI
                          </span>
                        )}
                      </div>
                      {it.alunno_classe && (
                        <div style={{ fontSize: 9, color: "#666", fontFamily: "monospace" }}>
                          cl. {it.alunno_classe}{it.alunno_anno_ultimo ? ` · ${String(it.alunno_anno_ultimo).slice(-2)}/${String((it.alunno_anno_ultimo+1)).slice(-2)}` : ""}
                        </div>
                      )}
                    </>
                  ) : (
                    <span style={{ color: "#ccc", fontStyle: "italic", fontSize: 10 }}>— non in anagrafica —</span>
                  )}
                </td>
                <td style={{ padding: "6px 8px", fontFamily: "monospace", fontWeight: 700, color: "#1a237e" }}>{it.cf_alunno}</td>
                <td style={{ padding: "6px 8px" }}>
                  <div style={{ fontWeight: 700 }}>{it.g1_cognome || ""} {it.g1_nome || ""}</div>
                  {it.g1_comune_recapito && <div style={{ fontSize: 9, color: "#888" }}>{it.g1_comune_recapito} · {it.g1_cap_recapito || ""}</div>}
                </td>
                <td style={{ padding: "6px 8px", fontFamily: "monospace", fontSize: 10 }}>{it.g1_cf || "—"}</td>
                <td style={{ padding: "6px 8px", fontSize: 10 }}>
                  {it.g1_email ? <a href={`mailto:${it.g1_email}`} style={{ color: "#0277bd" }}>{it.g1_email}</a> : <span style={{ color: "#ccc" }}>—</span>}
                </td>
                <td style={{ padding: "6px 8px", fontFamily: "monospace", fontSize: 10 }}>{it.g1_telefono || <span style={{ color: "#ccc" }}>—</span>}</td>
                <td style={{ padding: "6px 8px", borderLeft: "2px solid #e0e0e0" }}>
                  <div style={{ fontWeight: 700 }}>{it.g2_cognome || ""} {it.g2_nome || ""}</div>
                  {it.g2_comune_recapito && <div style={{ fontSize: 9, color: "#888" }}>{it.g2_comune_recapito} · {it.g2_cap_recapito || ""}</div>}
                </td>
                <td style={{ padding: "6px 8px", fontFamily: "monospace", fontSize: 10 }}>{it.g2_cf || "—"}</td>
                <td style={{ padding: "6px 8px", fontSize: 10 }}>
                  {it.g2_email ? <a href={`mailto:${it.g2_email}`} style={{ color: "#0277bd" }}>{it.g2_email}</a> : <span style={{ color: "#ccc" }}>—</span>}
                </td>
                <td style={{ padding: "6px 8px", fontFamily: "monospace", fontSize: 10 }}>{it.g2_telefono || <span style={{ color: "#ccc" }}>—</span>}</td>
                <td style={{ padding: "6px 8px", textAlign: "center" }}>
                  <button onClick={() => onDeleteRow(it._id, it.cf_alunno)} data-testid={`ag-del-${i}`}
                    title="Elimina questo record"
                    style={{ background: "#c62828", color: "white", border: "none", padding: "3px 7px", fontSize: 11, cursor: "pointer", borderRadius: 2 }}>🗑</button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = { padding: "6px 8px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: 0.3 };
const th2 = { padding: "3px 8px", textAlign: "left", fontSize: 9, fontWeight: 700, letterSpacing: 0.3 };
